import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type ReactRendererService from 'garaje/services/react-renderer';
import type StateService from 'garaje/services/state';
import type { MatchingRules } from 'garaje/utils/matching-rule-utils';
import { screenerVisitRoute } from 'garaje/utils/screener-visit-route';
import type { Root } from 'react-dom/client';
import type { ProfileMatchingRuleModal } from 'reactRichComponents/visitors/profiles/ProfileMatchingRuleModal';
import type { VisitorProfileShow } from 'reactRichComponents/visitors/profiles/VisitorProfileShow';

import { PREVIOUS_VISITS_PAGE_SIZE, UPCOMING_VISITS_PAGE_SIZE, type VisitorsProfilesShowModel } from './route';

type TabValue = 'visits' | 'aliases' | 'documents' | 'screeners' | 'identity';

export default class VisitorsProfilesShowController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare reactRenderer: ReactRendererService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;

  declare model: VisitorsProfilesShowModel;

  private modalRoot?: Root;
  // Paired so `remountReactProfileShow` can `rerender(component, root, props)`
  // without re-loading the chunk on every model swap.
  private reactProfileShowRoot?: Root;
  private reactProfileShowComponent?: typeof VisitorProfileShow;
  // Guards against a model swap during the initial async chunk fetch
  // queueing a second render() against the same root.
  private mountInFlight = false;
  // Sticky fallback to Ember if the React mount path throws.
  @tracked reactMountFailed = false;

  queryParams = ['upcomingVisitsCursor', 'previousVisitsCursor', 'from', 'id', 'tab'];
  @tracked upcomingVisitsCursor: string | null = null;
  @tracked previousVisitsCursor: string | null = null;
  @tracked from: string | null = null;
  @tracked id: string | null = null;
  @tracked tab: TabValue = 'visits';

  @tracked upcomingVisitsIsLoading: boolean = false;
  @tracked previousVisitsIsLoading: boolean = false;
  @tracked isLoadingRulesEditor: boolean = false;
  @tracked isSavingRules: boolean = false;

  get upcomingVisitsPageSize(): number {
    return UPCOMING_VISITS_PAGE_SIZE;
  }

  get previousVisitsPageSize(): number {
    return PREVIOUS_VISITS_PAGE_SIZE;
  }

  get canAccessDocumentUploads(): boolean {
    return this.abilities.can('access document-upload');
  }

  get canReadProfileScreeners(): boolean {
    return this.abilities.can('read-screeners visitor-profile');
  }

  /**
   * Gates the RRC Identity tab. The RRC side also requires the
   * `vis-profile-identity-tab` flag. `enable id-scanning` is a plan entitlement, not a location
   * setting; the third leg is a granular permission with no role-name fallback, so a Receptionist
   * does not inherit access.
   */
  get canReadIdentityDocuments(): boolean {
    return (
      this.abilities.can('read visitor-profile') &&
      this.abilities.can('enable id-scanning') &&
      this.abilities.can('read-profile-identity-documents id-scanning')
    );
  }

  get canAccessProfileNotes(): boolean {
    return this.featureFlags.isEnabled('vis-profile-notes');
  }

  get canEditRules(): boolean {
    return this.featureFlags.isEnabled('rule-based-profile-matching');
  }

  // Create gate for the "Add to blocklist" affordance (same ability the
  // entry/invite path uses). RRC also requires its own `vis-profile-blocklist`
  // flag; the backend is authoritative.
  get canManageBlocklist(): boolean {
    return this.abilities.can('create blocklist in security');
  }

  // Read gate for the blocked banner + "View on blocklist". Kept separate from
  // create so a read-only role still sees the banner, and a create-only role
  // isn't offered a View that dead-ends at the read-gated Security panel.
  get canViewBlocklist(): boolean {
    return this.abilities.can('read blocklist in security');
  }

  get activeTab(): TabValue {
    if (this.tab === 'documents' && !this.canAccessDocumentUploads) {
      return 'visits';
    }
    if (this.tab === 'screeners' && !this.canReadProfileScreeners) {
      return 'visits';
    }
    if (this.tab === 'identity' && !this.canReadIdentityDocuments) {
      return 'visits';
    }
    return this.tab;
  }

  /**
   * The Ember fallback tab strip only knows visits/aliases/documents, and its template is
   * `@glint-nocheck`, so a wider value crossing that boundary renders a strip with nothing
   * selected instead of failing to compile. Map the RRC-only tabs back to visits.
   */
  get emberActiveTab(): 'visits' | 'aliases' | 'documents' {
    const tab = this.activeTab;
    if (tab === 'aliases') return 'aliases';
    // activeTab has already cleared 'documents' when the ability is missing.
    if (tab === 'documents') return 'documents';
    return 'visits';
  }

  get backLink(): { route: string; model?: string; queryParams?: Record<string, string> } {
    // Log-level / settings routes (`visitors.entries`, `visitors.invites`,
    // `security.index`) have no dynamic segment, so omit `model` for those.
    // Other `from` values point at the single-record show route, which needs the source id.
    const isLogRoute = this.from === 'entries' || this.from === 'invites' || this.from === 'blocklist';
    // The blocklist badge deep-links a specific filter (`from=blocklist&id=<filter>`);
    // carry it as `blocklistFilterId` so Back expands the Block list panel on Security
    // (survives reload / opening the profile in a new tab). Panel-level today, matching
    // the security.index deep-link; row-level scroll/highlight for that id is a follow-up.
    const queryParams = this.from === 'blocklist' && this.id ? { blocklistFilterId: this.id } : undefined;
    return {
      route: this.#routeFrom,
      model: isLogRoute ? undefined : this.id || undefined,
      queryParams,
    };
  }

  @action
  setUpcomingVisitsCursor(cursor: string | null): void {
    this.upcomingVisitsIsLoading = true;
    this.upcomingVisitsCursor = cursor;
  }

  @action
  setPreviousVisitsCursor(cursor: string | null): void {
    this.previousVisitsIsLoading = true;
    this.previousVisitsCursor = cursor;
  }

  @action
  setTab(tab: TabValue): void {
    this.tab = tab;
  }

  // Dynamic import extracted so tests can override this to provide a fake
  // component without relying on webpack's module-federation chunk loader
  // (which doesn't resolve in the ember test env). Return type is sourced
  // from the `.d.ts` shim at types/reactRichComponents/visitors/profiles/.
  async loadProfileMatchingRuleModal(): Promise<typeof ProfileMatchingRuleModal> {
    const module = await import('reactRichComponents/visitors/profiles/ProfileMatchingRuleModal');
    return module.ProfileMatchingRuleModal;
  }

  @action
  async editRules(): Promise<void> {
    // Defense-in-depth: template gates the button on the flag, but guard the
    // action too in case it's invoked from devtools, stale DOM, or a test.
    if (!this.canEditRules) return;
    // Ignore re-clicks while the federated chunk is still loading.
    if (this.isLoadingRulesEditor) return;

    const currentRules = this.model.visitorProfile.matchingRules;
    this.isLoadingRulesEditor = true;
    this.rerenderReactProfileShowIfMounted();

    try {
      const ProfileMatchingRuleModal = await this.loadProfileMatchingRuleModal();

      if (this.isDestroying || this.isDestroyed) return;

      this.closeRulesModal();

      this.modalRoot = await this.reactRenderer.render(ProfileMatchingRuleModal, 'react__matching-rule-modal', {
        mode: 'profile' as const,
        companyId: this.state.currentCompany.id,
        initialRule: currentRules ?? undefined,
        isOpen: true,
        profileName: this.model.visitorProfile.displayName,
        onSave: (rule: MatchingRules) => void this.saveMatchingRules(rule),
        onClose: () => this.closeRulesModal(),
        onResetToDefault: (companyRules: MatchingRules) => void this.resetToDefaultRules(companyRules),
      });
    } catch {
      if (this.isDestroying || this.isDestroyed) return;
      this.flashMessages.showAndHideFlash('error', 'Failed to load matching rules editor');
      return;
    } finally {
      if (!this.isDestroying && !this.isDestroyed) {
        this.isLoadingRulesEditor = false;
        this.rerenderReactProfileShowIfMounted();
      }
    }

    if (this.isDestroying || this.isDestroyed) return;
    // Track outside the try/catch so a tracking failure cannot trigger the
    // load-error flash on a successfully rendered modal.
    this.#safeTrack('Profile Matching Rule Modal Opened', {
      source: 'profile',
      is_default: !currentRules,
      has_custom_rule: !!currentRules,
      group_count: currentRules?.v1?.or?.length ?? 0,
    });
  }

  // PATCH only the matching-rules attribute (backend rejects photo-url on
  // write). The response is pushed back into the store so the snapshot stays
  // in sync and rollbackAttributes() doesn't revert to the pre-PATCH value.
  async #patchMatchingRules(rule: MatchingRules): Promise<void> {
    const profileId = this.model.visitorProfile.id;
    const response = await this.ajaxFetch.patch<{
      data: { id: string; type: string; attributes: Record<string, unknown> };
    }>(`/a/visitors/api/v3/visitor-profiles/${profileId}`, {
      contentType: 'application/vnd.api+json',
      headers: { Accept: 'application/vnd.api+json' },
      data: JSON.stringify({
        data: {
          id: profileId,
          type: 'visitor-profiles',
          attributes: { 'matching-rules': rule },
        },
      }),
    });
    this.store.push(this.store.normalize('visitor-profile', response.data));
  }

  // Tracking failures must never break the UX flow.
  #safeTrack(event: string, properties: AnalyticsTraits): void {
    try {
      this.metrics.trackEvent(event, properties);
    } catch {
      /* swallow */
    }
  }

  async saveMatchingRules(rule: MatchingRules): Promise<void> {
    // Re-entry guard: prevents a double-click on the React Save button from
    // firing concurrent PATCHes while the first is still in flight.
    if (this.isSavingRules) return;
    this.isSavingRules = true;
    try {
      try {
        await this.#patchMatchingRules(rule);
      } catch {
        if (this.isDestroying || this.isDestroyed) return;
        this.flashMessages.showAndHideFlash('error', 'Failed to save matching rules');
        return;
      }
      if (this.isDestroying || this.isDestroyed) return;
      const displayName = this.model.visitorProfile.displayName;
      // closeRulesModal() swallows unmount errors internally, so this
      // sequential flow cannot trigger a false-success flash on cleanup failure.
      this.closeRulesModal();
      this.flashMessages.showAndHideFlash('success', `Custom rule saved for ${displayName}`);
      this.#safeTrack('Profile Matching Rule Saved', {
        source: 'profile',
        group_count: rule?.v1?.or?.length ?? 0,
        field_count: rule?.v1?.or?.reduce((sum, group) => sum + (group.and?.length ?? 0), 0) ?? 0,
      });
    } finally {
      if (!this.isDestroying && !this.isDestroyed) {
        this.isSavingRules = false;
      }
    }
  }

  async resetToDefaultRules(companyRules: MatchingRules): Promise<void> {
    // Shares isSavingRules with saveMatchingRules so the two paths cannot race.
    if (this.isSavingRules) return;
    this.isSavingRules = true;
    try {
      try {
        await this.#patchMatchingRules(companyRules);
      } catch {
        if (this.isDestroying || this.isDestroyed) return;
        this.flashMessages.showAndHideFlash('error', 'Failed to update matching rules');
        return;
      }
      if (this.isDestroying || this.isDestroyed) return;
      this.closeRulesModal();
      this.flashMessages.showAndHideFlash('success', 'Rule updated to company default');
      this.#safeTrack('Profile Matching Rule Reset to Default', {
        source: 'profile',
        group_count: companyRules?.v1?.or?.length ?? 0,
      });
    } finally {
      if (!this.isDestroying && !this.isDestroyed) {
        this.isSavingRules = false;
      }
    }
  }

  closeRulesModal(): void {
    if (this.modalRoot) {
      // Unmount errors (e.g. React root already torn down) must not leak
      // into the save path — the modal is going away regardless.
      try {
        this.reactRenderer.unmount(this.modalRoot);
      } catch {
        /* swallow */
      }
      this.modalRoot = undefined;
    }
  }

  // ─── React show-page mount (gated by `vis-visitor-profile-show-rrc`) ──────

  get useReactShowPage(): boolean {
    if (this.reactMountFailed) return false;
    return this.featureFlags.isEnabled('vis-visitor-profile-show-rrc');
  }

  // Extracted for test override.
  async loadVisitorProfileShow(): Promise<typeof VisitorProfileShow> {
    const module = await import('reactRichComponents/visitors/profiles/VisitorProfileShow');
    return module.VisitorProfileShow;
  }

  #buildReactProfileShowProps() {
    return {
      profileId: this.model.visitorProfile.id,
      initialTab: this.activeTab,
      onNavigateBack: () => this.handleBackNavigation(),
      canReadProfileVisits: this.abilities.can('read profile visits'),
      canAccessDocumentUploads: this.canAccessDocumentUploads,
      canAccessProfileNotes: this.canAccessProfileNotes,
      canManageNote: this.abilities.can('manage-note visitor-profile'),
      canReadProfileScreeners: this.canReadProfileScreeners,
      canReadIdentityDocuments: this.canReadIdentityDocuments,
      canEditRules: this.canEditRules,
      isLoadingRulesEditor: this.isLoadingRulesEditor,
      onEditRules: () => void this.editRules(),
      onInvite: () => this.inviteFromProfile(),
      // Blocklist actions. RRC decides Add (not blocked) vs View (blocked); the
      // host supplies the create gate for Add, the read gate for View + banner,
      // and both callbacks. onViewOnBlocklist's filterId is optional (forward-compat).
      canManageBlocklist: this.canManageBlocklist,
      canViewBlocklist: this.canViewBlocklist,
      onAddToBlocklist: () => this.addToBlocklist(),
      onViewOnBlocklist: (filterId?: string) => this.viewOnBlocklist(filterId),
      // RRC renders the frequent-host name as a link when this returns
      // a string and the visit-stats response includes a non-null
      // frequent-host-id; falls back to plain text otherwise.
      getEmployeeHref: (hostId: string) => this.router.urlFor('employees.directory.employee', hostId),
      // RRC's Screeners-tab View buttons hand back the composite visit id of
      // the screener's run; route to the matching entry/invite details page.
      onViewScreenerVisit: (visitId: string) => this.viewScreenerVisit(visitId),
      // RRC Visits-tab rows route to the entry/invite details — restores the
      // legacy Ember datetime-cell clickability, with the same returnTo
      // threading (Back returns to the Visits tab). The gating context mirrors
      // the Ember `shouldLinkToVisit` gate (current-location + list permission).
      onViewVisit: (visitId: string) => this.viewVisit(visitId),
      currentLocationId: this.currentLocation.location?.id,
      canViewAllEntries: this.abilities.can('view all entries'),
      canViewAllInvites: this.abilities.can('view all invites'),
    };
  }

  // A blank id or unknown composite type no-ops (screenerVisitRoute returns
  // null) rather than transitioning to a broken route; the destination route
  // remains the authoritative permission gate. Both wrappers are public (not
  // `#private`) so the returnTo threading is unit-testable, the same way the
  // sibling `handleBackNavigation` action is invoked from tests.
  viewScreenerVisit(visitId: string): void {
    this.#routeToVisit(visitId, 'screeners');
  }

  viewVisit(visitId: string): void {
    // Preserve the legacy Ember datetime-cell click-through analytics event so
    // the funnel stays continuous for companies on the React profile page.
    this.#safeTrack('Visitors Profiles Visits Table Click Into Visit', { visit_id: visitId });
    this.#routeToVisit(visitId, 'visits');
  }

  // Routes to the entry/invite details page for a composite visit id, threading
  // `returnTo` so the destination's Back button returns to this profile on the
  // originating tab (Screeners or Visits) instead of the invites/entries log.
  #routeToVisit(visitId: string, returnToTab: 'screeners' | 'visits'): void {
    const target = screenerVisitRoute(visitId);
    if (!target) return;
    void this.router.transitionTo(target.route, target.model, {
      queryParams: {
        returnTo: 'visitors.profiles.show',
        returnToModel: this.model.visitorProfile.id,
        returnToTab,
      },
    });
  }

  @action
  async mountReactProfileShow(_element: HTMLElement): Promise<void> {
    if (!this.useReactShowPage) return;
    if (this.isDestroying || this.isDestroyed) return;
    if (this.mountInFlight || this.reactProfileShowRoot) return;
    this.mountInFlight = true;

    try {
      this.reactProfileShowComponent = await this.loadVisitorProfileShow();
      if (this.isDestroying || this.isDestroyed) return;

      this.reactProfileShowRoot = await this.reactRenderer.render(
        this.reactProfileShowComponent,
        'react__visitor-profile-show',
        this.#buildReactProfileShowProps(),
      );
    } catch (err) {
      if (this.isDestroying || this.isDestroyed) return;
      this.reactMountFailed = true;
      this.flashMessages.showAndHideFlash('error', 'Failed to load updated profile view');
      this.#safeTrack('Visitor Profile Show Mount Failed', {
        profile_id: this.model.visitorProfile.id,
        error_message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      this.mountInFlight = false;
    }
  }

  @action
  unmountReactProfileShow(_element: HTMLElement): void {
    if (this.reactProfileShowRoot) {
      try {
        this.reactRenderer.unmount(this.reactProfileShowRoot);
      } catch {
        /* root may have torn itself down already */
      }
      this.reactProfileShowRoot = undefined;
      this.reactProfileShowComponent = undefined;
    }
  }

  // Push the new model id into the existing React root via `rerender` —
  // avoids the createRoot race that an unmount+remount cycle would have.
  @action
  remountReactProfileShow(element: HTMLElement, [_profileId]: [string]): void {
    if (this.isDestroying || this.isDestroyed) return;
    if (this.mountInFlight) return;
    if (!this.reactProfileShowRoot || !this.reactProfileShowComponent) {
      void this.mountReactProfileShow(element);
      return;
    }
    this.reactRenderer.rerender(
      this.reactProfileShowComponent,
      this.reactProfileShowRoot,
      this.#buildReactProfileShowProps(),
    );
  }

  // Re-render after mutating a tracked prop the React side reads.
  rerenderReactProfileShowIfMounted(): void {
    if (!this.reactProfileShowRoot || !this.reactProfileShowComponent) return;
    if (this.isDestroying || this.isDestroyed) return;
    this.reactRenderer.rerender(
      this.reactProfileShowComponent,
      this.reactProfileShowRoot,
      this.#buildReactProfileShowProps(),
    );
  }

  @action
  handleBackNavigation(): void {
    // Mirror `backLink` rather than branching on `this.id`: log routes have no
    // dynamic segment, so passing a positional model throws (the original bug).
    const { route, model, queryParams } = this.backLink;
    if (queryParams) {
      void this.router.transitionTo(route, { queryParams });
    } else if (model) {
      void this.router.transitionTo(route, model);
    } else {
      void this.router.transitionTo(route);
    }
  }

  @action
  inviteFromProfile(): void {
    const { displayName, primaryEmail, id } = this.model.visitorProfile;
    void this.router.transitionTo('visitors.invites.new', {
      queryParams: {
        name: displayName,
        email: primaryEmail ?? undefined,
        // Threaded so the back button on visitors.invites.new returns to
        // this profile instead of the invites log.
        returnTo: 'visitors.profiles.show',
        returnToModel: id,
      },
    });
  }

  // Navigate to Security → Block list with the profile id as a query param; the
  // security route seeds the pre-filled create modal from it (VIS-17399).
  @action
  addToBlocklist(): void {
    this.#safeTrack('Visitor Profile - Add to Block List Button Clicked', {
      visitor_profile_id: this.model.visitorProfile.id,
    });
    void this.router.transitionTo('security', {
      queryParams: { addBlocklistProfileId: this.model.visitorProfile.id },
    });
  }

  // "View on blocklist" / blocked-banner "Review". Routes to the blocklist
  // settings; when a filterId is provided (see board Q: — RRC does not pass one
  // yet), the security.index `blocklistFilterId` query param expands the panel
  // for that entry. Without one, `openBlockList` still expands the panel so the
  // matching records aren't hidden behind a collapsed section.
  viewOnBlocklist(filterId?: string): void {
    // Explicit branch: `security` has no dynamic segment, so passing an options
    // object only when there's a query param avoids Ember treating `{}` as a
    // route model ("more context objects than dynamic segments").
    // typeof guard: RRC invokes this from an onClick, where a bare handler
    // reference would pass the click event — a truthy non-string would become
    // `blocklistFilterId=[object Object]` in the URL. Treat it as no-filterId.
    if (typeof filterId === 'string' && filterId) {
      void this.router.transitionTo('security', { queryParams: { blocklistFilterId: filterId } });
    } else {
      void this.router.transitionTo('security', { queryParams: { expandBlockList: true } });
    }
  }

  get #routeFrom(): string {
    switch (this.from) {
      case 'entry':
        return 'visitors.entry';
      case 'invite':
        return 'visitors.invites.show';
      // Log-level back targets — used by the row-level hover card so the
      // back button returns to the list the user was browsing, not to the
      // single-record show page (which they hadn't visited).
      case 'entries':
        return 'visitors.entries';
      case 'invites':
        return 'visitors.invites';
      // Row-level badge on the blocklist table returns to Security → Block list.
      case 'blocklist':
        return 'security.index';
      default:
        return 'visitors.profiles';
    }
  }
}
